import { StatusCodes } from "http-status-codes";
import {
  IAllCompleteHealthCare,
  IAllHealthCareFetchResponse,
  IBillingUrlFormResponse,
  IHealthCare,
  IHealthCareDoctorForm,
  IHealthCareFilter,
  IHealthCareSchemeForm,
  IHealthCareRequiredDocument,
  IHealthCareResponse,
  IManageUserForm,
  IPostBillingUrlFormResponse,
  IPostHealthCareDoctorResponse,
  IPostHealthCareSchemeResponse,
  IPostManageUserResponse,
  IPostSchemeSpecificLoginDetailsResponse,
  ISchemeSpecificLoginDetailsForm,
  IHealthCareScheme,
} from "../../interfaces/IHealthCare";
import Axios from "../../utils/axiosInterceptor";
import { HEALTH_CARE_APIS } from "../../utils/constants";

export const fetchHealthCares = async (
  filters: IHealthCareFilter
): Promise<IAllHealthCareFetchResponse> => {
  try {
    const response = await Axios.post(
      HEALTH_CARE_APIS.ALL_HEALTH_CARES,
      filters
    );
    const healthCareResp: IAllHealthCareFetchResponse = response.data;

    if (response.status === StatusCodes.OK) {
      return {
        healthCareResponseList: healthCareResp.healthCareResponseList,
        totalElements: healthCareResp.totalElements,
        totalPages: healthCareResp.totalPages,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all health cares",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all health cares:", error);
    throw new Error("Cannot fetch all health cares");
  }
};

export const postHealthCare = async (
  formData: IHealthCare
): Promise<IHealthCareResponse> => {
  try {
    const response = await Axios.post(
      HEALTH_CARE_APIS.ADD_HEALTHCARE,
      formData
    );
    const healthCareResp: IHealthCare = response.data.healthCare;

    if (response.status === StatusCodes.OK) {
      return {
        healthCareRes: healthCareResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post health care",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post health care");
  }
};

export const postHealthCareScheme = async (
  healthCareId: number,
  schemeId: number,
  formData: IHealthCareScheme
): Promise<IPostHealthCareSchemeResponse> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/schemes/${schemeId}/health_care_schemes`,
      formData
    );
    const schemeResp: IHealthCareScheme = response.data;
    if (response.status === StatusCodes.CREATED) {
      return {
        schemeRes: schemeResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post health care scheme",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post health care scheme");
  }
};

export const postHealthCareDoctor = async (
  healthCareId: number,
  formData: IHealthCareDoctorForm
): Promise<IPostHealthCareDoctorResponse> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_doctors`,
      formData
    );
    const doctorResp: IHealthCareDoctorForm = response.data;
    if (response.status === StatusCodes.CREATED) {
      return {
        doctorRes: doctorResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post health care doctor",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post health care doctor");
  }
};

export const postManageUser = async (
  healthCareId: number,

  formData: IManageUserForm
): Promise<IPostManageUserResponse> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_management_user`,
      formData
    );
    const manageUserResp: IManageUserForm = response.data;
    if (response.status === StatusCodes.CREATED) {
      return {
        manageUserRes: manageUserResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post health care doctor",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post health care doctor");
  }
};

export const postSchemeSpecificLoginDetails = async (
  healthCareId: number,
  formData: ISchemeSpecificLoginDetailsForm
): Promise<IPostSchemeSpecificLoginDetailsResponse> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_mis_credentials_scheme_specific`,
      formData
    );
    const schemeSpecificLoginDetailsResp: ISchemeSpecificLoginDetailsForm =
      response.data;
    if (response.status === StatusCodes.CREATED) {
      return {
        schemeSpecificLoginDetailsRes: schemeSpecificLoginDetailsResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post scheme specific login details",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post scheme specific login details");
  }
};

export const postBillingUrlForm = async (
  healthCareId: number,

  formData: IBillingUrlFormResponse
): Promise<IPostBillingUrlFormResponse> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_billing_credentials`,
      formData
    );
    const billingUrlFormResp: IBillingUrlFormResponse = response.data;
    if (response.status === StatusCodes.CREATED) {
      return {
        billingUrlFormRes: billingUrlFormResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post billing url form",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post billing url form");
  }
};

export const postHealthCareRequiredDocument = async (
  healthCareId: number,
  formData: IHealthCareRequiredDocument
): Promise<IAllCompleteHealthCare> => {
  try {
    const response = await Axios.post(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_required_documents`,
      formData
    );
    const healthCareRequiredDocumentsResp: IHealthCareRequiredDocument[] =
      response.data.healthCareRequiredDocuments;
    if (response.status === StatusCodes.CREATED) {
      return {
        healthCareRequiredDocumentsRes: healthCareRequiredDocumentsResp,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot post health care required document",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post health care required document");
  }
};
